import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/* ─── Font Import ─────────────────────────────────────────────────────────── */
const FontImport = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,750;12..96,800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

    @keyframes orbDrift {
      0%,100% { transform: translate(0,0) scale(1); opacity:.55; }
      33%      { transform: translate(18px,-12px) scale(1.07); opacity:.75; }
      66%      { transform: translate(-10px,20px) scale(.96); opacity:.5; }
    }
    @keyframes beamSlide {
      0%   { transform: translateX(-60%) rotate(-35deg); opacity:0; }
      30%  { opacity:1; }
      70%  { opacity:.6; }
      100% { transform: translateX(60%) rotate(-35deg); opacity:0; }
    }
    @keyframes nodePulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(30,144,255,0.55), 0 0 6px rgba(30,144,255,0.7); }
      50%     { box-shadow: 0 0 0 6px rgba(30,144,255,0), 0 0 14px rgba(30,144,255,0.9); }
    }
    @keyframes ringPulse {
      0%   { transform: scale(.85); opacity:.9; }
      100% { transform: scale(2.2); opacity:0; }
    }
    @keyframes dataFlow {
      0%   { stroke-dashoffset: 200; opacity:0; }
      15%  { opacity:1; }
      85%  { opacity:.6; }
      100% { stroke-dashoffset: 0; opacity:0; }
    }
    @keyframes metricCount {
      0%   { opacity:0; transform: translateY(4px); }
      100% { opacity:1; transform: translateY(0); }
    }
    @keyframes scanLine {
      0%   { transform: translateY(-100%); opacity:0; }
      10%  { opacity:.35; }
      90%  { opacity:.25; }
      100% { transform: translateY(100%); opacity:0; }
    }
    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes floatY {
      0%,100% { transform: translateY(0px); }
      50%     { transform: translateY(-10px); }
    }
    @keyframes glowPulse {
      0%,100% { opacity:.3; }
      50%     { opacity:.7; }
    }
  `}</style>
);

/* ─── Seeded deterministic RNG ───────────────────────────────────────────── */
const rng = (s) => { const x = Math.sin(s + 1) * 10000; return x - Math.floor(x); };

/* ─── Particle data ──────────────────────────────────────────────────────── */
const PARTICLES = Array.from({ length: 38 }, (_, i) => ({
  id: i,
  x: rng(i * 3.14) * 100,
  y: rng(i * 2.71) * 100,
  size: rng(i * 1.41) * 2.6 + 0.5,
  opacity: rng(i * 1.61) * 0.18 + 0.03,
  durY: rng(i * 5.55) * 22 + 14,
  durX: rng(i * 7.77) * 16 + 10,
  dy: (rng(i * 4.44) - 0.5) * 50,
  dx: (rng(i * 6.66) - 0.5) * 32,
  isBlue: rng(i * 9.99) > 0.42,
  dlyY: rng(i * 2.22) * -22,
  dlyX: rng(i * 3.33) * -16,
  layer: Math.floor(rng(i * 8.88) * 3),
}));

/* ─── Neural network node positions ─────────────────────────────────────── */
const NODES = [
  { id: 0, x: 50, y: 20, size: 5, primary: true },
  { id: 1, x: 20, y: 38, size: 4, primary: false },
  { id: 2, x: 80, y: 35, size: 4.5, primary: false },
  { id: 3, x: 38, y: 56, size: 3.5, primary: false },
  { id: 4, x: 65, y: 60, size: 4, primary: false },
  { id: 5, x: 15, y: 65, size: 3, primary: false },
  { id: 6, x: 85, y: 62, size: 3.5, primary: false },
  { id: 7, x: 50, y: 78, size: 5, primary: true },
  { id: 8, x: 30, y: 82, size: 3, primary: false },
  { id: 9, x: 72, y: 80, size: 3.5, primary: false },
  { id: 10, x: 8,  y: 48, size: 2.5, primary: false },
  { id: 11, x: 92, y: 50, size: 2.5, primary: false },
];

const EDGES = [
  [0,1],[0,2],[0,3],[0,4],
  [1,3],[1,5],[2,4],[2,6],
  [3,7],[4,7],[5,8],[6,9],
  [7,8],[7,9],[1,10],[2,11],
  [3,4],[5,3],[6,4],
];

const METRICS = [
  { label: "Model Accuracy",  value: "99.4%",  delta: "+2.1%",  up: true  },
  { label: "Inference Speed", value: "12ms",   delta: "-38%",   up: true  },
  { label: "Knowledge Nodes", value: "2.4B",   delta: "+18.3%", up: true  },
  { label: "Active Contexts", value: "847K",   delta: "+5.7%",  up: true  },
];

const CARD_ITEMS = [
  { text: "Privacy-First AI Architecture",    icon: "◈" },
  { text: "Sovereign, On-Prem & Controlled AI Systems",        icon: "◉" },
  { text: "Human–AI Synchronized Intelligence",      icon: "◆" },
  { text: "Real-Time Knowledge & Decision Support",      icon: "◎" },
  { text: "Enterprise-Grade Security & Trust",         icon: "◇" },
  { text: "Ethical and Responsible AI Design",    icon: "◈" },
];

/* ─── fadeUp helper ──────────────────────────────────────────────────────── */
const fadeUp = (delay = 0, y = 30) => ({
  initial: { opacity: 0, y },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

/* ─── Particle Field ─────────────────────────────────────────────────────── */
const ParticleField = () => (
  <Box aria-hidden sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 1 }}>
    {PARTICLES.map((p) => (
      <Box
        key={p.id}
        component={motion.div}
        animate={{ y: [0, p.dy, 0], x: [0, p.dx, 0] }}
        transition={{
          y: { duration: p.durY, repeat: Infinity, ease: "easeInOut", delay: p.dlyY },
          x: { duration: p.durX, repeat: Infinity, ease: "easeInOut", delay: p.dlyX },
        }}
        sx={{
          position: "absolute",
          left: `${p.x}%`, top: `${p.y}%`,
          width: `${p.size}px`, height: `${p.size}px`,
          borderRadius: "50%",
          background: p.isBlue
            ? `rgba(30,144,255,${p.opacity * (p.layer === 2 ? 4 : p.layer === 1 ? 2.8 : 1.8)})`
            : `rgba(255,255,255,${p.opacity * (p.layer === 2 ? 1.4 : 1)})`,
          boxShadow: p.isBlue && p.size > 2
            ? `0 0 ${p.size * 4}px rgba(30,144,255,0.5)`
            : "none",
          willChange: "transform",
          filter: p.layer === 2 ? "blur(0px)" : p.layer === 1 ? "blur(0.4px)" : "blur(0.8px)",
        }}
      />
    ))}
  </Box>
);

/* ─── Neural Network SVG Panel ───────────────────────────────────────────── */
const NeuralPanel = () => {
  const [activeEdge, setActiveEdge] = useState(0);
  const [activeNode, setActiveNode] = useState(0);

  useEffect(() => {
    const edgeTimer = setInterval(() => {
      setActiveEdge(e => (e + 1) % EDGES.length);
    }, 600);
    const nodeTimer = setInterval(() => {
      setActiveNode(n => (n + 1) % NODES.length);
    }, 900);
    return () => { clearInterval(edgeTimer); clearInterval(nodeTimer); };
  }, []);

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: 380 }}>
      {/* Main panel glow */}
      <Box sx={{
        position: "absolute", inset: "-30%",
        background: "radial-gradient(ellipse at 50% 50%, rgba(30,144,255,0.12) 0%, transparent 65%)",
        filter: "blur(40px)",
        pointerEvents: "none",
        animation: "glowPulse 4s ease-in-out infinite",
      }}/>

      {/* SVG Neural Net */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.94 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        sx={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <svg
          viewBox="0 0 100 100"
          style={{ width: "100%", height: "100%", maxWidth: 340, maxHeight: 340, overflow: "visible" }}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <radialGradient id="nodeGrad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(30,144,255,0.9)" />
              <stop offset="100%" stopColor="rgba(30,144,255,0.1)" />
            </radialGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
              <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>

          {/* Edges */}
          {EDGES.map(([a, b], i) => {
            const na = NODES[a], nb = NODES[b];
            const isActive = activeEdge === i;
            return (
              <line
                key={i}
                x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                stroke={isActive ? "rgba(30,144,255,0.75)" : "rgba(30,144,255,0.12)"}
                strokeWidth={isActive ? 0.45 : 0.2}
                style={{
                  transition: "stroke 0.3s ease, stroke-width 0.3s ease",
                  filter: isActive ? "url(#glow)" : "none",
                }}
              />
            );
          })}

          {/* Animated data pulse along active edge */}
          {(() => {
            const [a, b] = EDGES[activeEdge];
            const na = NODES[a], nb = NODES[b];
            return (
              <motion.circle
                key={activeEdge}
                cx={na.x} cy={na.y}
                r={1.1}
                fill="rgba(30,144,255,0.95)"
                filter="url(#glow)"
                animate={{ cx: [na.x, nb.x], cy: [na.y, nb.y], opacity: [0, 1, 1, 0] }}
                transition={{ duration: 0.55, ease: "easeInOut" }}
              />
            );
          })()}

          {/* Nodes */}
          {NODES.map((node) => {
            const isActive = activeNode === node.id;
            return (
              <g key={node.id}>
                {isActive && (
                  <motion.circle
                    cx={node.x} cy={node.y}
                    r={node.size * 2}
                    fill="none"
                    stroke="rgba(30,144,255,0.4)"
                    strokeWidth={0.3}
                    animate={{ r: [node.size, node.size * 3.5], opacity: [0.7, 0] }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                  />
                )}
                <circle
                  cx={node.x} cy={node.y}
                  r={node.size}
                  fill={node.primary ? "rgba(30,144,255,0.9)" : "rgba(30,144,255,0.45)"}
                  filter="url(#glow)"
                  style={{ transition: "fill 0.3s ease, r 0.3s ease" }}
                />
                <circle
                  cx={node.x} cy={node.y}
                  r={node.size * 0.4}
                  fill="rgba(180,220,255,0.95)"
                />
              </g>
            );
          })}
        </svg>
      </Box>
    </Box>
  );
};

/* ─── Intelligence Panel (Right side) ───────────────────────────────────── */
const IntelligencePanel = () => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 2000);
    return () => clearInterval(t);
  }, []);

  return (
    <motion.div {...fadeUp(0.28)}>
      <Box
        component={motion.div}
        whileHover={{ y: -6, transition: { duration: 0.3, ease: "easeOut" } }}
        sx={{
          position: "relative",
          borderRadius: "24px",
          overflow: "hidden",
          background: "linear-gradient(160deg, rgba(10,20,40,0.92) 0%, rgba(5,10,20,0.97) 100%)",
          border: "1px solid rgba(30,144,255,0.18)",
          backdropFilter: "blur(28px)",
          WebkitBackdropFilter: "blur(28px)",
          transition: "border-color 0.3s ease, box-shadow 0.3s ease",
          "&:hover": {
            borderColor: "rgba(30,144,255,0.38)",
            boxShadow: "0 24px 80px rgba(30,144,255,0.14), 0 0 0 1px rgba(30,144,255,0.12)",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0, left: "10%", right: "10%",
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(30,144,255,0.6), transparent)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0, left: "20%", right: "20%",
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(30,144,255,0.2), transparent)",
          },
        }}
      >
        {/* Corner glow lights */}
        <Box sx={{
          position: "absolute", top: -60, right: -60,
          width: 200, height: 200,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(30,144,255,0.16), transparent 70%)",
          filter: "blur(24px)",
          pointerEvents: "none",
          animation: "glowPulse 5s ease-in-out infinite",
        }}/>
        <Box sx={{
          position: "absolute", bottom: -40, left: -40,
          width: 160, height: 160,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(30,144,255,0.1), transparent 70%)",
          filter: "blur(20px)",
          pointerEvents: "none",
          animation: "glowPulse 7s ease-in-out infinite reverse",
        }}/>

        {/* Scan line */}
        <Box sx={{
          position: "absolute", top: 0, left: 0, right: 0,
          height: "30%",
          background: "linear-gradient(to bottom, transparent, rgba(30,144,255,0.03), transparent)",
          animation: "scanLine 6s linear infinite",
          pointerEvents: "none",
          zIndex: 2,
        }}/>

        {/* Panel header */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: { xs: 2.5, md: 3 }, pt: { xs: 2.5, md: 3 }, pb: 2,
          borderBottom: "1px solid rgba(30,144,255,0.1)",
          position: "relative", zIndex: 3,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 32, height: 32,
              borderRadius: "10px",
              background: "rgba(30,144,255,0.1)",
              border: "1px solid rgba(30,144,255,0.24)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Box sx={{
                width: 10, height: 10,
                borderRadius: "3px",
                background: "#1E90FF",
                boxShadow: "0 0 10px rgba(30,144,255,0.9)",
                animation: "nodePulse 2.4s ease-in-out infinite",
              }}/>
            </Box>
            <Box>
              <Typography sx={{
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontWeight: 700,
                fontSize: "0.88rem",
                color: "rgba(255,255,255,0.9)",
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}>
                AI Intelligence Core
              </Typography>
              <Typography sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.7rem",
                color: "rgba(30,144,255,0.7)",
                fontWeight: 400,
                lineHeight: 1,
                mt: 0.3,
              }}>
                Anthrasync Runtime v3.2
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{
              width: 5, height: 5, borderRadius: "50%",
              background: "#22d3a1",
              boxShadow: "0 0 8px rgba(34,211,161,0.8)",
              animation: "nodePulse 2s ease-in-out infinite",
            }}/>
            <Typography sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.68rem",
              color: "rgba(34,211,161,0.85)",
              fontWeight: 500,
            }}>
              LIVE
            </Typography>
          </Box>
        </Box>

        {/* Neural network visualization */}
        <Box sx={{
          px: { xs: 2, md: 2.5 }, pt: 1, pb: 0,
          height: { xs: 200, sm: 240, md: 260 },
          position: "relative", zIndex: 3,
        }}>
          <NeuralPanel />
        </Box>

        {/* Metrics grid */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "1px",
          mx: { xs: 2.5, md: 3 },
          mb: { xs: 2.5, md: 3 },
          borderRadius: "14px",
          overflow: "hidden",
          border: "1px solid rgba(30,144,255,0.12)",
          position: "relative", zIndex: 3,
        }}>
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 + i * 0.07, ease: [0.16, 1, 0.3, 1] }}
            >
              <Box
                component={motion.div}
                whileHover={{ background: "rgba(30,144,255,0.07)" }}
                sx={{
                  p: { xs: "10px 12px", md: "12px 16px" },
                  background: "rgba(30,144,255,0.025)",
                  borderRight: i % 2 === 0 ? "1px solid rgba(30,144,255,0.1)" : "none",
                  borderBottom: i < 2 ? "1px solid rgba(30,144,255,0.1)" : "none",
                  transition: "background 0.2s ease",
                }}
              >
                <Typography sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.65rem",
                  color: "rgba(255,255,255,0.38)",
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  mb: 0.6,
                }}>
                  {m.label}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.8 }}>
                  <Typography sx={{
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    fontWeight: 750,
                    color: "rgba(255,255,255,0.92)",
                    letterSpacing: "-0.02em",
                    lineHeight: 1,
                  }}>
                    {m.value}
                  </Typography>
                  <Typography sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "0.66rem",
                    color: m.up ? "rgba(34,211,161,0.85)" : "rgba(255,80,80,0.85)",
                    fontWeight: 600,
                  }}>
                    {m.delta}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>

        {/* Status bar */}
        <Box sx={{
          mx: { xs: 2.5, md: 3 }, mb: { xs: 2.5, md: 3 },
          p: "10px 14px",
          borderRadius: "10px",
          background: "rgba(30,144,255,0.04)",
          border: "1px solid rgba(30,144,255,0.1)",
          display: "flex", alignItems: "center", gap: 1.5,
          position: "relative", zIndex: 3,
        }}>
          <Box sx={{
            width: 4, height: 4, borderRadius: "50%",
            background: "#1E90FF",
            boxShadow: "0 0 6px rgba(30,144,255,0.8)",
            flexShrink: 0,
            animation: "nodePulse 1.8s ease-in-out infinite",
          }}/>
          <Typography sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.7rem",
            color: "rgba(255,255,255,0.4)",
            fontWeight: 400,
          }}>
            Processing {Math.floor(847 + (tick % 3) * 12)}K contexts across distributed inference mesh
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
};

/* ─── Mouse-reactive glow ─────────────────────────────────────────────────── */
const MouseGlow = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const springX = useSpring(mx, { stiffness: 60, damping: 28 });
  const springY = useSpring(my, { stiffness: 60, damping: 28 });
  const glowX = useTransform(springX, [0, 1], ["0%", "100%"]);
  const glowY = useTransform(springY, [0, 1], ["0%", "100%"]);

  const handleMouseMove = useCallback((e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  }, [mx, my]);

  return (
    <Box
      ref={ref}
      onMouseMove={handleMouseMove}
      aria-hidden
      sx={{ position: "absolute", inset: 0, pointerEvents: "all", zIndex: 1 }}
    >
      <motion.div
        style={{
          position: "absolute",
          width: 560,
          height: 560,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(30,144,255,0.055) 0%, transparent 65%)",
          filter: "blur(60px)",
          left: glowX,
          top: glowY,
          transform: "translate(-50%,-50%)",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const AboutHeroSection = () => (
  <>
    <FontImport />

    <Box
      component="section"
      sx={{
        position: "relative",
        minHeight: "90vh",
        display: "flex",
        alignItems: "center",
        backgroundColor: "#000000",
        color: "#ffffff",
        overflow: "hidden",
        fontFamily: "'Bricolage Grotesque', sans-serif",
      }}
    >
      {/* ── Animated grid ── */}
      <Box aria-hidden sx={{
        position: "absolute", inset: 0,
        backgroundImage:
          "linear-gradient(rgba(30,144,255,0.025) 1px, transparent 1px)," +
          "linear-gradient(90deg, rgba(30,144,255,0.025) 1px, transparent 1px)",
        backgroundSize: "72px 72px",
        maskImage: "radial-gradient(ellipse 75% 70% at 50% 50%, black 0%, transparent 100%)",
        pointerEvents: "none",
        zIndex: 1,
      }}/>

      {/* ── Ambient orbs ── */}
      <Box aria-hidden sx={{
        position: "absolute", top: "-20%", right: "-12%",
        width: { xs: 280, md: 600 }, height: { xs: 280, md: 600 },
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(30,144,255,0.1) 0%, transparent 68%)",
        filter: "blur(60px)",
        animation: "orbDrift 12s ease-in-out infinite",
        pointerEvents: "none", zIndex: 0,
      }}/>
      <Box aria-hidden sx={{
        position: "absolute", bottom: "-25%", left: "-14%",
        width: { xs: 240, md: 500 }, height: { xs: 240, md: 500 },
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(30,144,255,0.07) 0%, transparent 68%)",
        filter: "blur(70px)",
        animation: "orbDrift 15s ease-in-out infinite reverse",
        animationDelay: "-4s",
        pointerEvents: "none", zIndex: 0,
      }}/>

      {/* ── Gradient beams ── */}
      <Box aria-hidden sx={{
        position: "absolute", top: "10%", left: "-20%",
        width: "80%", height: "2px",
        background: "linear-gradient(90deg, transparent 0%, rgba(30,144,255,0.35) 40%, rgba(30,144,255,0.55) 55%, transparent 100%)",
        transform: "rotate(-15deg)",
        filter: "blur(1px)",
        animation: "beamSlide 9s ease-in-out infinite",
        animationDelay: "0s",
        pointerEvents: "none", zIndex: 1,
      }}/>
      <Box aria-hidden sx={{
        position: "absolute", top: "50%", left: "-30%",
        width: "90%", height: "1px",
        background: "linear-gradient(90deg, transparent 0%, rgba(30,144,255,0.2) 45%, rgba(30,144,255,0.35) 60%, transparent 100%)",
        transform: "rotate(-12deg)",
        filter: "blur(0.5px)",
        animation: "beamSlide 12s ease-in-out infinite",
        animationDelay: "-5s",
        pointerEvents: "none", zIndex: 1,
      }}/>

      {/* ── Spotlight behind heading ── */}
      <Box
        component={motion.div}
        animate={{ opacity: [0.08, 0.18, 0.08] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden
        sx={{
          position: "absolute", top: "0%", left: { xs: "10%", md: "5%" },
          width: { xs: "80%", md: "55%" }, height: { xs: 200, md: 340 },
          borderRadius: "50%",
          background: "radial-gradient(ellipse, rgba(30,144,255,0.1) 0%, transparent 70%)",
          filter: "blur(50px)",
          pointerEvents: "none", zIndex: 1,
        }}
      />

      {/* ── Particles ── */}
      <ParticleField />

      {/* ── Mouse glow ── */}
      <MouseGlow />

      {/* ─── CONTENT ──────────────────────────────────────────────────────── */}
      <Container
        maxWidth="lg"
        sx={{
          position: "relative", zIndex: 4,
          px: { xs: "20px", sm: "32px", md: "48px", lg: "64px" },
          py: { xs: "80px", sm: "96px", md: "112px" },
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Grid
          container
          spacing={{ xs: 4, sm: 5, md: 7, lg: 9 }}
          alignItems="center"
        >

          {/* ══ LEFT — text ══ */}
          <Grid item xs={12} md={6} lg={6.5}>

            {/* Overline badge */}
            <motion.div {...fadeUp(0.05)}>
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 1.4,
                px: { xs: 1.6, md: 2 }, py: { xs: 0.6, md: 0.75 },
                mb: { xs: 3.5, md: 4 },
                borderRadius: "999px",
                background: "rgba(30,144,255,0.06)",
                border: "1px solid rgba(30,144,255,0.2)",
                position: "relative",
              }}>
                {/* Ping animation */}
                <Box
                  component={motion.div}
                  animate={{ scale: [1, 2.6], opacity: [0.5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                  sx={{
                    position: "absolute",
                    width: 6, height: 6,
                    borderRadius: "50%",
                    background: "#1E90FF",
                    left: 12,
                  }}
                />
                <Box sx={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: "#1E90FF",
                  boxShadow: "0 0 12px rgba(30,144,255,0.9)",
                  flexShrink: 0, zIndex: 1,
                }}/>
                <Typography sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: { xs: "0.69rem", sm: "0.73rem" },
                  fontWeight: 600,
                  color: "rgba(30,144,255,0.85)",
                  letterSpacing: "0.13em",
                  textTransform: "uppercase",
                }}>
                  About Anthrasync
                </Typography>
              </Box>
            </motion.div>

            {/* Heading — editorial large */}
            <motion.div {...fadeUp(0.12)}>
              <Typography
                component="h1"
                sx={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: "2.5rem", sm: "3.1rem", md: "3.6rem", lg: "4.1rem", xl: "4.6rem" },
                  lineHeight: { xs: 1.1, md: 1.06 },
                  letterSpacing: { xs: "-1.5px", md: "-2.5px" },
                  color: "rgba(255,255,255,0.97)",
                  mb: 0,
                }}
              >
                Building Sovereign, Privacy-First{" "}
                <Box
                  component="span"
                  sx={{
                    display: "inline",
                    background:"#1E90FF",
                    // background: "linear-gradient(135deg, #1E90FF 0%, #60B4FF 38%, #93C5FD 65%, #BFDBFE 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    backgroundSize: "200% auto",
                    // animation: "shimmer 5s linear infinite",
                  }}
                >
                  Human–AI Systems
                </Box>
              </Typography>
            </motion.div>

            {/* Description */}
            <motion.div {...fadeUp(0.2)}>
              <Typography sx={{
                fontFamily: "'DM Sans', sans-serif",
                mt: { xs: 3, md: 3.5 },
                maxWidth: { xs: "100%", md: 580 },
                fontSize: { xs: "0.95rem", sm: "1rem", md: "1.07rem" },
                lineHeight: 1.9,
                color: "rgba(255,255,255,0.52)",
                fontWeight: 400,
              }}>
                Anthrasync is an AI deeptech company focused on building privacy-first, sovereign, and human-synchronized AI systems that augment how people and organizations think, learn, and make decisions.
We design next-generation AI infrastructure where humans and intelligent systems work in sync—securely, transparently, and in real time.
              </Typography>
            </motion.div>

            {/* Defining items — inline list */}
            <motion.div {...fadeUp(0.28)}>
              <Stack spacing={0} sx={{ mt: { xs: 4, md: 4.5 } }}>
                {CARD_ITEMS.map((item, i) => (
                  <motion.div
                    key={item.text}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.32 + i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Box
                      component={motion.div}
                      whileHover={{ x: 4, transition: { duration: 0.18 } }}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1.8,
                        py: "8px",
                        borderBottom: i < CARD_ITEMS.length - 1
                          ? "1px solid rgba(255,255,255,0.045)"
                          : "none",
                        cursor: "default",
                        "&:hover .item-dot": {
                          boxShadow: "0 0 10px rgba(30,144,255,0.9)",
                          background: "#4DABFF",
                        },
                        "&:hover .item-text": {
                          color: "rgba(255,255,255,0.75)",
                        },
                      }}
                    >
                      <Box
                        className="item-dot"
                        sx={{
                          width: 5, height: 5, borderRadius: "50%",
                          background: "#1E90FF",
                          flexShrink: 0,
                          boxShadow: "0 0 6px rgba(30,144,255,0.7)",
                          transition: "all 0.22s ease",
                        }}
                      />
                      <Typography
                        className="item-text"
                        sx={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: { xs: "0.86rem", md: "0.91rem" },
                          color: "rgba(255,255,255,0.5)",
                          fontWeight: 400,
                          lineHeight: 1.5,
                          transition: "color 0.22s ease",
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Stack>
            </motion.div>
          </Grid>

        
        </Grid>
      </Container>

      {/* ── Bottom vignette ── */}
      <Box aria-hidden sx={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        height: "100px",
        background: "linear-gradient(to top, #000000 0%, transparent 100%)",
        pointerEvents: "none", zIndex: 3,
      }}/>
    </Box>
  </>
);

export default AboutHeroSection;